import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const CreatePostModal = ({ isOpen, onClose, onSubmit }) => {
  const [postContent, setPostContent] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [donationAmount, setDonationAmount] = useState('');
  const [selectedCharity, setSelectedCharity] = useState('');
  const [postType, setPostType] = useState('story');

  const charityOptions = [
    { value: 'red-cross', label: 'American Red Cross' },
    { value: 'unicef', label: 'UNICEF' },
    { value: 'doctors-without-borders', label: 'Doctors Without Borders' },
    { value: 'habitat', label: 'Habitat for Humanity' },
    { value: 'feeding-america', label: 'Feeding America' }
  ];

  const postTypeOptions = [
    { value: 'story', label: 'Share Impact Story' },
    { value: 'donation', label: 'Celebrate Donation' },
    { value: 'challenge', label: 'Challenge Update' }
  ];

  const handleImageUpload = (e) => {
    const file = e?.target?.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => setSelectedImage(e?.target?.result);
      reader?.readAsDataURL(file);
    }
  };

  const handleSubmit = () => {
    if (postContent?.trim()) {
      const newPost = {
        content: postContent,
        image: selectedImage,
        type: postType,
        donation: postType === 'donation' ? {
          amount: parseFloat(donationAmount),
          charity: charityOptions?.find(c => c?.value === selectedCharity)?.label
        } : null,
        timestamp: new Date()
      };
      
      onSubmit(newPost);
      
      // Reset form
      setPostContent('');
      setSelectedImage(null);
      setDonationAmount('');
      setSelectedCharity('');
      setPostType('story');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-100 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-card-foreground">Create Post</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Post Type Selection */}
          <Select
            label="Post Type"
            options={postTypeOptions}
            value={postType}
            onChange={setPostType}
            className="mb-4"
          />

          {/* User Info */}
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-lg font-medium">
              JD
            </div>
            <div>
              <p className="font-semibold text-card-foreground">John Doe</p>
              <p className="text-sm text-muted-foreground">Sharing with community</p>
            </div>
          </div>

          {/* Post Content */}
          <textarea
            value={postContent}
            onChange={(e) => setPostContent(e?.target?.value)}
            placeholder={
              postType === 'story' ? "Share your impact story..." :
              postType === 'donation' ? "Tell us about your donation..." :
              "Share your challenge progress..."
            }
            className="w-full h-32 p-3 border border-border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />

          {/* Donation Details (if donation post) */}
          {postType === 'donation' && (
            <div className="space-y-4 p-4 bg-primary/5 border border-primary/20 rounded-lg">
              <h3 className="font-medium text-card-foreground flex items-center space-x-2">
                <Icon name="Heart" size={18} className="text-primary" />
                <span>Donation Details</span>
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Donation Amount"
                  type="number"
                  placeholder="0.00"
                  value={donationAmount}
                  onChange={(e) => setDonationAmount(e?.target?.value)}
                />
                
                <Select
                  label="Charity"
                  options={charityOptions}
                  value={selectedCharity}
                  onChange={setSelectedCharity}
                  placeholder="Select charity"
                />
              </div>
            </div>
          )}

          {/* Image Upload */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-card-foreground">
              Add Photo (Optional)
            </label>
            
            {selectedImage ? (
              <div className="relative">
                <div className="rounded-lg overflow-hidden bg-muted">
                  <Image
                    src={selectedImage}
                    alt="Selected image"
                    className="w-full h-48 object-cover"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedImage(null)}
                  className="absolute top-2 right-2 bg-black/50 text-white hover:bg-black/70"
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:border-primary/50 transition-micro">
                <Icon name="ImagePlus" size={32} className="text-muted-foreground mb-2" />
                <span className="text-sm text-muted-foreground">Click to upload image</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Privacy Notice */}
          <div className="flex items-start space-x-2 p-3 bg-muted/50 rounded-lg">
            <Icon name="Info" size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
            <p className="text-sm text-muted-foreground">
              Your post will be visible to all community members. Personal information like donation amounts can be kept private in your settings.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" size="sm">
              <Icon name="Image" size={16} />
            </Button>
            <Button variant="ghost" size="sm">
              <Icon name="MapPin" size={16} />
            </Button>
            <Button variant="ghost" size="sm">
              <Icon name="Smile" size={16} />
            </Button>
          </div>
          
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={!postContent?.trim()}
            >
              Share Post
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePostModal;